/** @jsx jsx */
import { jsx } from '@emotion/core';

import { FunctionComponent, useContext, useRef } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Typography, Card, CardContent, Grid } from '@material-ui/core';
import PasswordTextField from '../../../password-text-field';
import PGEButton from '../../../buttons';
import useFormState from '../../../../hooks/useFormState';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import PasswordIndicatorList from '../../../password-indicator-list';
import { isValidPassword } from '../../../../util/form-validation';
import { RouteComponentProps } from '@reach/router';
import theme from '../../../../themes/theme';
import validationRules from './ChangePassword.rules';
import useUtil from '../../../../hooks/pge-plus/useUtil';
import useContentMgmt from '../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';
import colors from '../../../../themes/main-colors';
import { Link, navigate } from 'gatsby';
import ROUTES from '../../../../routes';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import useInvoicePaymentAuth from '../../../../hooks/invoice-payment-portal/useInvoicePaymentAuth';
import { updatePasswordMutation } from '../../../../queries/changePassword.query';
import { useApolloClient } from '@apollo/react-hooks';
import { useServiceProviderInfo } from '../../../../hooks/invoice-payment-portal/useInvoicePaymentAccountInfo';

const useStyles = makeStyles(() =>
  createStyles({
    actionArea: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    submitButton: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  }),
);

const ChangePassword: FunctionComponent<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const classes = useStyles();

  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();

  const { content } = useContentMgmt(
    microcopyGroupIds.INVOICE_PAYMENT_CHANGE_PASSWORD,
  );

   const apolloClient = useApolloClient();
    const notificationContext = useContext(NotificationsContext);
    const { signInWithCustomToken, authUser } = useInvoicePaymentAuth();
    const passwordUpdatedRef = useRef(false);
    const signInTokenRef = useRef<string | null>(null);
    const { encryptedPersonId } = useServiceProviderInfo();
    
    // const location = useLocation();
    // const redirectUrl =
    //   ((location.state || {}) as RouterState)?.redirectUrl || undefined;

  const doSubmit = async (
      data: any,
      encryptedPersonid: string | null,
    ): Promise<void> => {
      passwordUpdatedRef.current = false;
      signInTokenRef.current = null;
      await apolloClient
        .mutate({
          mutation: updatePasswordMutation,
          variables: {
            payload: {
              password: data.password,
              encryptedPersonId: encryptedPersonid,
            },
          },
          context: { invoicePayment: true },
        })
        .then(result => {
          const updatePasswordResponse = result?.data?.updatePassword;
          if (
            !updatePasswordResponse?.success ||
            !updatePasswordResponse.signInToken
          ) {
            onFailure();
          } else {
            passwordUpdatedRef.current = true;
            signInTokenRef.current = updatePasswordResponse.signInToken;
          }
        });
      return Promise.resolve();
    };
  
    const onFailure = (): void => {
      notificationContext.setState({
        isOpen: true,
        message: content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE'),
        severity: 'error',
      });
    };
  
    const onSuccess = async (username: string): Promise<void> => {
      if (!passwordUpdatedRef.current) {
        return;
      }
  
      if (!username) {
        onFailure();
        return;
      }
  
      const signInToken = signInTokenRef.current;
      if (!signInToken) {
        onFailure();
        return;
      }
  
      try {
        await signInWithCustomToken(signInToken, username);
      } catch (error) {
        onFailure();
        return;
      }
  
      passwordUpdatedRef.current = false;
  
      notificationContext.setState({
        isOpen: true,
        severity: 'success',
        variant: 'filled',
        message: content.get('CHANGE_PASSWORD_SUCCESS'),
      });  
   
      return navigate(ROUTES.INVOICE_PAYMENT_PORTAL_INVOICE_LIST);
    };
  const form = useFormState(
    {
      password: '',
    },
    {
      validate: validationRules,
    },
  );

  const onSubmit = wrapWithLoader(
    form.submit(async data => {
     await doSubmit(data, encryptedPersonId!);
     await onSuccess(authUser?.email);
    
    }),
  );

  return (
    <form noValidate onSubmit={onSubmit}>
      <Grid container spacing={2} css={{ marginTop: theme.spacing(2) }}>
        <Grid
          xs={12}
          item
          css={{
            marginTop: 16,
          }}
        >
          <span css={{ color: colors.sparkBlue }}>{'<'}</span>
          <Link
            to={ROUTES.INVOICE_PAYMENT_PORTAL_INVOICE_LIST}
            css={{
              fontSize: '18px',
              fontWeight: 700,
            }}
          >
            {content.get('BACK_TO_INVOICE_LIST')}
          </Link>
        </Grid>
        <Grid item>
          <Typography variant={'h1'}>
            {content.get('CHANGE_PASSWORD_BUTTON')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant={'h2'}>
                    {content.get('ENTER_NEW_PASSWORD')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <PasswordTextField
                    name="password"
                    style={{ width: '100%' }}
                    inputProps={{ minLength: 8, maxLength: 16 }}
                    {...form.props('password')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PasswordIndicatorList password={form.values.password} />
                </Grid>
                <Grid item xs={12} className={classes.actionArea}>
                  <PGEButton
                    className={classes.submitButton}
                    type={'submit'}
                    variant="contained"
                    color="primary"
                    disabled={!isValidPassword(form.values.password)}
                  >
                    {content.get('CHANGE_PASSWORD_BUTTON')}
                  </PGEButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
};

export default ChangePassword;
